// api.js
import axios from 'axios';
import { API_BASE_URL, USUARIOACCION, FECHAHORA, LOGERRORINSERT, AUDITORIA, TIEMPOEXPIRACIONCOOKIEENMINUTOS } from '../services/apiConfig';

// Función para obtener el token
const getAuthToken = () => {
  return sessionStorage.getItem("authToken") || '';
};

export const registrarAccion = async (usuario, navega) => {
  try {
    const TOKEN = getAuthToken();
    const response = await axios.post(API_BASE_URL + USUARIOACCION, {
      usuarioid: usuario,
      navego: navega,
    }, {
      headers: TOKEN ? { 'Authorization': `Bearer ${TOKEN}` } : {}
    });
    
    if (response.status === 200) {
      console.log('Registro de acción exitoso:', response.data);
    } else {
      console.error('Error al registrar la acción:', response.status);
      registrarLogError(usuario, navega, response.status);
    }
  } catch (error) {
    console.error('Error al realizar la solicitud:', error.message);
    registrarLogError(usuario, navega, JSON.stringify(error));
  }
};

export const registrarLogError = async (usuario, aplicacion, error) => {
  try {
    const TOKEN = getAuthToken();
    const response = await axios.post(API_BASE_URL + LOGERRORINSERT, {
      usuarioid: usuario,
      error: error,
      aplicacion: aplicacion,
    }, {
      headers: TOKEN ? { 'Authorization': `Bearer ${TOKEN}` } : {}
    });
    
    if (response.status === 200) {
      console.log('Registro de error exitoso:', response.data);
    } else {
      console.error('Error al registrar el error:', response.status);
    }
  } catch (error) {
    console.error('Error al realizar la solicitud:', error.message);
  }
};

// Aviso para la pantalla de login: la sesión se cerró porque venció (no porque
// el usuario haya salido). Login.jsx lo lee y lo borra. Vive en sessionStorage
// porque sobrevive a la recarga completa de la página que hace el redirect.
export const FLAG_SESION_VENCIDA = 'sesion_vencida';

// El backend firma el JWT con su propio vencimiento (claim `exp`, ver
// auth/login.php) y NO lo renueva. Leerlo acá evita que el front se crea
// logueado después de que el servidor ya no acepta el token. Si no se puede
// leer el token, se devuelve false y decide el servidor (con un 401).
const jwtVencido = (token) => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4)));
    return typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now();
  } catch {
    return false;
  }
};

export const isAuthenticated = (componente) => {
  const token = sessionStorage.getItem('authToken');
  const expiryTimeStr = sessionStorage.getItem('expiryTime');
  const currentTime = new Date().getTime();
  const loggedIn = sessionStorage.getItem('loggedIn');
  const habiaSesion = !!(loggedIn && token);

  if (habiaSesion && expiryTimeStr && currentTime < parseInt(expiryTimeStr) && !jwtVencido(token)) {
    const maxAgeMs = (TIEMPOEXPIRACIONCOOKIEENMINUTOS || 15) * 60 * 1000;
    const newExpiryTime = currentTime + maxAgeMs;
    sessionStorage.setItem("expiryTime", newExpiryTime.toString());
    return true;
  }

  if (componente) {
    registrarLogError('unlogin', componente, 'usuario no logueado. token: ' + token);
  }

  // Si había una sesión y dejó de ser válida, el login avisa que venció. La
  // marca tiene que sobrevivir al clear(): este guard se vuelve a evaluar
  // (re-render, StrictMode) antes de que Login llegue a leerla, y esa segunda
  // pasada ya no ve sesión.
  const avisarVencimiento = habiaSesion || sessionStorage.getItem(FLAG_SESION_VENCIDA) === '1';
  sessionStorage.removeItem('authToken');
  sessionStorage.removeItem('expiryTime');
  sessionStorage.removeItem('loggedIn');
  sessionStorage.clear();
  if (avisarVencimiento) sessionStorage.setItem(FLAG_SESION_VENCIDA, '1');

  return false;
};

export const registrarAuditoria = async (accion, modulo, registro, identificador, observaciones, origen, usuarioid) => {
  try {
    const TOKEN = getAuthToken();
    const response = await axios.post(API_BASE_URL + AUDITORIA, {
      accion: accion,
      modulo: modulo,
      registro: registro,
      identificador: identificador,
      observaciones: observaciones,
      origen: origen,
      usuarioid: usuarioid
    }, {
      headers: TOKEN ? { 'Authorization': `Bearer ${TOKEN}` } : {}
    });
  } catch (error) {
    console.error('Error al registrar auditoría:', error);
  }
};

export const getServerDateTime = async () => {
  try {
    const response = await axios.get(API_BASE_URL + FECHAHORA);
    return response.data.serverDateTime;
  } catch (error) {
    console.error('Error al obtener la fecha y hora del servidor:', error);
    throw error;
  }
};

export const getServerHora = async () => {
  try {
    const response = await axios.get(API_BASE_URL + FECHAHORA);
    return response.data.serverHora;
  } catch (error) {
    console.error('Error al obtener la hora del servidor:', error);
    throw error;
  }
};

// Normaliza la respuesta del backend a un shape consistente.
// El backend devuelve históricamente 3 formatos distintos:
//   1) { status: 'success'|'error', message, data }
//   2) { success: true|false, message, data }
//   3) Array crudo  [ ... ]   (o un objeto plano sin status/success)
// Este interceptor agrega aliases cruzados (status <-> success) para que los
// componentes puedan leer indistintamente cualquiera de los dos campos.
// No envuelve arrays para no romper consumidores actuales que iteran response.data
// directamente — esos consumidores deben pasar al patrón defensivo
// `response.data?.data ?? response.data` antes del Commit 3 (backend standardization).
const normalizeResponseData = (data) => {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) return data;
  const hasStatus = Object.prototype.hasOwnProperty.call(data, 'status');
  const hasSuccess = Object.prototype.hasOwnProperty.call(data, 'success');
  if (hasSuccess && !hasStatus) {
    data.status = data.success ? 'success' : 'error';
  } else if (hasStatus && !hasSuccess) {
    data.success = data.status === 'success';
  }
  return data;
};

// Aviso/auditoría de errores de negocio en escrituras que responden HTTP 200.
// createResponse() nunca setea un código != 200 en su rama de error, así que
// un POST/PUT/DELETE que falló server-side (ej: columna inexistente, sin
// turno abierto) resuelve como éxito para axios. Si la pantalla que llamó
// no valida `status`/`success` a mano, la operación queda silenciada: se ve
// como guardada pero no pasó nada. Para no depender de que cada pantalla lo
// chequee, cualquier error de negocio en una escritura se avisa (toast
// global) y audita (tabla errores) igual que un error de transporte.
const avisarErrorDeNegocioEnEscritura = (response) => {
  const method = (response?.config?.method || 'get').toUpperCase();
  if (!['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) return;

  const url = response?.config?.url || '';
  if (url.includes('logerror') || url.includes('login') || url.includes('register')) return;
  if (response?.data?.status !== 'error') return;

  const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
  const usuarioid = userData?.usuarioid || 'unlogin';
  const mensaje = response.data.message || 'El servidor rechazó la operación.';
  let shortUrl = url;
  try { shortUrl = url.replace(/^https?:\/\/[^/]+/, ''); } catch {}

  // Sin payload: algunas escrituras llevan datos sensibles (contraseñas,
  // certificados) y esta tabla no es el lugar para guardarlos.
  registrarLogError(usuarioid, url, JSON.stringify({
    ruta: window.location.href, metodo: method, status: 200, mensaje,
  }, null, 2));

  window.dispatchEvent(new CustomEvent('api-error', {
    detail: { title: 'No se pudo guardar', message: mensaje, severity: 'error', status: 200, url: shortUrl, method }
  }));
};

axios.interceptors.response.use(
  (response) => {
    if (response?.config?.responseType !== 'blob' && response?.config?.responseType !== 'arraybuffer') {
      response.data = normalizeResponseData(response.data);
    }
    avisarErrorDeNegocioEnEscritura(response);
    return response;
  },
  (error) => {
    // Sesión vencida o inválida: el backend responde 401 siempre que el token
    // falta, venció o no corresponde (config/funciones.php); los problemas de
    // permisos son 403. Se cierra la sesión local y se vuelve al login en vez de
    // dejar navegar pantallas que ya no pueden traer datos. Va ANTES de loguear
    // el error: con el token vencido el propio registro también daría 401.
    // (No aplica al login ni al registro de errores, ni si ya estamos en /login.)
    const urlReq = error.config?.url || '';
    if (error.response?.status === 401 && !urlReq.includes('login') && !urlReq.includes('logerror')
        && typeof window !== 'undefined' && window.location.pathname !== '/login') {
      sessionStorage.clear();
      sessionStorage.setItem(FLAG_SESION_VENCIDA, '1');
      window.location.href = '/login';
      // Promise que nunca resuelve: el .catch() del componente que hizo el
      // pedido no llega a mostrar su propio error justo al irnos de la pantalla.
      return new Promise(() => {});
    }

    const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
    const usuarioid = userData?.usuarioid || 'unlogin';
    const url = error.config?.url || 'unknown';
    const method = (error.config?.method || 'GET').toUpperCase();
    const status = error.response?.status || 'network';
    const statusText = error.response?.statusText || error.message || 'Sin conexión';
    
    // Payload enviado (body de POST/PUT)
    let payload = '';
    try {
      if (error.config?.data) {
        const parsed = typeof error.config.data === 'string'
          ? JSON.parse(error.config.data)
          : error.config.data;
        payload = JSON.stringify(parsed).slice(0, 500);
      }
    } catch { payload = String(error.config?.data || '').slice(0, 500); }

    // Params de query string (GET)
    let params = '';
    try {
      if (error.config?.params) {
        params = JSON.stringify(error.config.params).slice(0, 300);
      }
    } catch { params = ''; }

    // Respuesta del servidor (si llegó)
    let responseBody = '';
    try {
      if (error.response?.data) {
        const raw = typeof error.response.data === 'object'
          ? JSON.stringify(error.response.data)
          : String(error.response.data);
        responseBody = raw.slice(0, 1000);
      }
    } catch { responseBody = ''; }

    const errorDetail = {
      ruta: window.location.href,
      metodo: method,
      status,
      mensaje: statusText,
      payload: payload || undefined,
      params: params || undefined,
      respuesta: responseBody || undefined,
    };

    // No loguear errores del propio logerror (evita loops infinitos)
    if (url.includes('logerror')) return Promise.reject(error);

    // Pedidos opcionales (config.silent = true): un fallo no se avisa ni se
    // audita; el que llama decide qué mostrar. El 401 ya se trató arriba.
    if (error.config?.silent) return Promise.reject(error);

    registrarLogError(usuarioid, url, JSON.stringify(errorDetail, null, 2));

    // Notificar al usuario vía toast global (escuchado por GlobalErrorToast).
    // 401 se omite porque el componente ya redirige al login.
    if (status !== 401 && typeof window !== 'undefined') {
      let title = 'Error de conexión';
      let message = statusText || 'Ocurrió un error al comunicarse con el servidor';
      let severity = 'error';

      if (status === 'network') {
        title = 'Sin conexión';
        message = 'No se pudo contactar al servidor. Verificá tu conexión.';
      } else if (status === 404) {
        title = 'Recurso no encontrado';
        message = 'El endpoint solicitado no existe (404).';
      } else if (status === 403) {
        title = 'Acceso denegado';
        message = 'No tenés permisos para realizar esta acción.';
        severity = 'warning';
      } else if (status === 429) {
        title = 'Demasiadas solicitudes';
        message = 'Esperá un momento antes de reintentar.';
        severity = 'warning';
      } else if (typeof status === 'number' && status >= 500) {
        title = 'Error del servidor';
        message = `El servidor devolvió un error (${status}). Si persiste, contactá al administrador.`;
      } else if (typeof status === 'number' && status >= 400) {
        title = 'Error en la solicitud';
        // Si el backend envió un mensaje legible, usarlo
        try {
          const body = error.response?.data;
          if (body && typeof body === 'object' && body.message) {
            message = body.message;
          }
        } catch {}
      }

      // Path corto sin el base URL, para mostrar
      let shortUrl = url;
      try { shortUrl = url.replace(/^https?:\/\/[^/]+/, ''); } catch {}

      window.dispatchEvent(new CustomEvent('api-error', {
        detail: { title, message, severity, status, url: shortUrl, method }
      }));
    }

    return Promise.reject(error);
  }
);
